import { Suspense, useState, useRef, useEffect } from 'react'
import { Canvas, useThree } from '@react-three/fiber'
import { OrbitControls, Environment } from '@react-three/drei'
import * as THREE from 'three'

// Componente de cerámica individual
const CeramicTile = ({ position, size = 0.3, materialProps }) => {
  return (
    <mesh position={position} castShadow receiveShadow>
      <boxGeometry args={[size, 0.01, size]} />
      <meshPhysicalMaterial {...materialProps} />
    </mesh>
  )
}

// Grid de cerámicas
const CeramicTilesGrid = ({ rows = 30, cols = 20, tileSize = 0.3, ...materialProps }) => {
  const tiles = []
  
  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      const x = (i - rows / 2) * tileSize + tileSize / 2
      const z = (j - cols / 2) * tileSize + tileSize / 2
      
      tiles.push(
        <CeramicTile 
          key={`${i}-${j}`}
          position={[x, 0, z]}
          size={tileSize}
          materialProps={materialProps}
        />
      )
    }
  }
  
  return <>{tiles}</>
}

// Componente Scene principal
const Scene = ({ controls }) => {
  return (
    <>
      <ambientLight intensity={0.2} />
      <directionalLight
        position={[10, 10, 5]}
        intensity={1}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
      />
      
      <CeramicTilesGrid
        rows={30}
        cols={20}
        tileSize={0.3}
        color={controls.tileColor}
        roughness={controls.roughness}
        metalness={controls.metalness}
        clearcoat={controls.clearcoat}
        clearcoatRoughness={controls.clearcoatRoughness}
      />
      
      {/* Environment con ground projection */}
      <Environment 
        preset="city"
        background
        ground={{
          height: controls.height,
          radius: controls.radius,
          scale: 1000
        }}
      />
      
      <OrbitControls
        target={[0, 0.5, 0]}
        maxPolarAngle={Math.PI / 2}
        maxDistance={80}
        minDistance={5}
        enablePan={false}
      />
    </>
  )
}

// Componente Perspective principal
export const Perspective = () => {
  const [controls, setControls] = useState({
    height: 15,
    radius: 100,
    grounded: true,
    tileColor: '#ffffff',
    roughness: 0.1,
    metalness: 0.0,
    clearcoat: 0.5,
    clearcoatRoughness: 0.1
  })

  return (
    <div style={{
      display: 'flex',
      width: '100vw',
      height: '100vh',
      overflow: 'hidden'
    }}>
      {/* Panel de controles */}
      <div style={{
        width: '320px',
        background: 'rgba(30, 30, 30, 0.8)',
        padding: '20px',
        color: 'white',
        boxShadow: '2px 0 15px rgba(0,0,0,0.4)',
        overflowY: 'auto',
        backdropFilter: 'blur(10px)',
        borderRight: '1px solid rgba(255,255,255,0.1)'
      }}>
        <h2 style={{ 
          borderBottom: '1px solid rgba(255,255,255,0.2)', 
          paddingBottom: '12px',
          marginBottom: '20px',
          color: '#aaa'
        }}>
          CERAMIC TILE CONTROLS
        </h2>
        
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9em' }}>Tile Color:</label>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input
              type="color"
              value={controls.tileColor}
              onChange={(e) => setControls({...controls, tileColor: e.target.value})}
              style={{ 
                width: '50px', 
                height: '30px',
                marginRight: '10px',
                cursor: 'pointer'
              }}
            />
            <span>{controls.tileColor.toUpperCase()}</span>
          </div>
        </div>
        
        {['roughness', 'metalness', 'clearcoat', 'clearcoatRoughness', 'height', 'radius'].map((prop) => (
          <div key={prop} style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9em' }}>
              {{
                roughness: `Roughness: ${controls.roughness.toFixed(2)}`,
                metalness: `Metalness: ${controls.metalness.toFixed(2)}`,
                clearcoat: `Clearcoat: ${controls.clearcoat.toFixed(2)}`,
                clearcoatRoughness: `Clearcoat Roughness: ${controls.clearcoatRoughness.toFixed(2)}`,
                height: `Ground Height: ${controls.height.toFixed(1)}`,
                radius: `Ground Radius: ${controls.radius.toFixed(1)}`
              }[prop]}
            </label>
            <input
              type="range"
              min={0}
              max={prop.includes('Roughness') || prop === 'metalness' ? 1 : 
                   prop === 'height' ? 30 : 
                   prop === 'radius' ? 200 : 1}
              step={0.01}
              value={controls[prop]}
              onChange={(e) => setControls({...controls, [prop]: parseFloat(e.target.value)})}
              style={{ 
                width: '100%',
                height: '6px',
                borderRadius: '3px',
                background: 'linear-gradient(90deg, #333, #555)',
                outline: 'none'
              }}
            />
          </div>
        ))}
        
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '20px' }}>
          <input
            type="checkbox"
            id="grounded"
            checked={controls.grounded}
            onChange={(e) => setControls({...controls, grounded: e.target.checked})}
            style={{ marginRight: '10px' }}
          />
          <label htmlFor="grounded" style={{ fontSize: '0.9em' }}>Enable Ground Projection</label>
        </div>
      </div>

      {/* Vista 3D */}
      <div style={{ flex: 1 }}>
        <Canvas
          gl={{
            antialias: true,
            toneMapping: THREE.ACESFilmicToneMapping,
            toneMappingExposure: 1.0
          }}
          camera={{ position: [-20, 7, 20], fov: 40 }}
        >
          <Suspense fallback={null}>
            <Scene controls={controls} />
          </Suspense>
        </Canvas>
      </div>
    </div>
  )
}

export default Perspective